import {
  Box,
  Card,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconPlusOutline } from '@zillow/constellation-icons';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { date50YearsAgo, isValidUSPhoneNumber, today } from '../../../../utils/formatters';
import { MaskedPhoneInput } from '../../../Inputs';
import { DatePicker } from '../../../DatePicker';

const emptyPastJob = {
  title: '',
  employer: {
    organization: '',
    name: '',
    email: '',
    phone: '',
  },
  startDate: '',
  endDate: '',
};

export function PastJobsStep() {
  const { fields, append, remove } = useFieldArray({ name: 'pastJobs' });

  function onDeleteRow(index: number) {
    remove(index);
  }

  function onAddRow() {
    append(emptyPastJob);
  }

  return (
    <>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Past jobs
      </Heading>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {fields.map((pastJob, index) => (
          <PastJobEdit key={pastJob.id} index={index} onDeleteRow={() => onDeleteRow(index)} />
        ))}
        <TextButton onClick={onAddRow} icon={<IconPlusOutline />} type="button">
          Add past job
        </TextButton>
      </Box>
    </>
  );
}

function PastJobEdit({ index, onDeleteRow }: { index: number; onDeleteRow: () => void }) {
  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext();

  const errorTitle = get(errors, `pastJobs[${index}].title`);
  const errorOrganization = get(errors, `pastJobs[${index}].employer.organization`);
  const errorName = get(errors, `pastJobs[${index}].employer.name`);
  const errorEmail = get(errors, `pastJobs[${index}].employer.email`);
  const errorPhone = get(errors, `pastJobs[${index}].employer.phone`);

  const startDateValue = watch(`pastJobs[${index}].startDate`);

  return (
    <>
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Heading level={3} textStyle="heading-xs" css={{ color: 'text.subtle' }}>
          Past job #{index + 1}
        </Heading>
        <TextButton onClick={onDeleteRow} type="button">
          Delete
        </TextButton>
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        <FormField
          label={<Label>Job title</Label>}
          control={
            <Input
              maxLength={32}
              {...register(`pastJobs[${index}].title`, {
                required: 'A job title is required',
              })}
            />
          }
          description={errorTitle?.message ? <FormHelp>{String(errorTitle.message)}</FormHelp> : undefined}
          error={!!errorTitle}
        />

        <FormField
          label={<Label>Employer</Label>}
          control={
            <Input
              maxLength={32}
              {...register(`pastJobs[${index}].employer.organization`, {
                required: 'An employer is required',
              })}
            />
          }
          description={
            errorOrganization?.message ? <FormHelp>{String(errorOrganization.message)}</FormHelp> : undefined
          }
          error={!!errorOrganization}
        />
        <Controller
          name={`pastJobs[${index}].startDate`}
          rules={{
            required: 'A started-on date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label>Started on</Label>}
              description={error?.message ? <FormHelp>{error.message}</FormHelp> : undefined}
              error={invalid}
            >
              <DatePicker {...field} minDate={date50YearsAgo()} maxDate={today()} />
            </FormField>
          )}
        />

        <Controller
          name={`pastJobs[${index}].endDate`}
          rules={{
            required: 'An ended-on date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
                mustBeAfter: {
                  value: startDateValue,
                  message: 'Must be after started-on date',
                },
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label>Ended on</Label>}
              description={error?.message ? <FormHelp>{error.message}</FormHelp> : undefined}
              error={invalid}
            >
              <DatePicker {...field} minDate={date50YearsAgo()} maxDate={today()} />
            </FormField>
          )}
        />
        <FormField
          label={<Label>Employer contact name</Label>}
          control={
            <Input
              maxLength={100}
              {...register(`pastJobs[${index}].employer.name`, { required: 'Employer contact name is required' })}
            />
          }
          description={
            errorName?.message ? (
              <FormHelp>{String(errorName.message)}</FormHelp>
            ) : (
              <FormHelp>
                Add the name of your manager or an HR representative who can verify your employment at this company
              </FormHelp>
            )
          }
          error={!!errorName}
        />
        <FormField
          label={<Label>Employer contact email</Label>}
          control={
            <Input
              maxLength={100}
              {...register(`pastJobs[${index}].employer.email`, {
                validate: validateEmail,
              })}
              type="email"
            />
          }
          description={errorEmail?.message ? <FormHelp>{String(errorEmail.message)}</FormHelp> : undefined}
          error={!!errorEmail}
          optional
        />
        <FormField
          label={<Label id={`pastJobs${index}-employer-phone-label`}>Employer contact phone</Label>}
          control={
            <Controller
              name={`pastJobs[${index}].employer.phone`}
              rules={{
                validate: (phone) => {
                  if (!phone) return true;
                  return isValidUSPhoneNumber(phone) || 'Enter a valid phone number';
                },
              }}
              render={({ field }) => (
                <MaskedPhoneInput {...field} aria-labelledby={`pastJobs${index}-employer-phone-label`} />
              )}
            />
          }
          description={errorPhone?.message ? <FormHelp>{String(errorPhone.message)}</FormHelp> : undefined}
          error={!!errorPhone}
          optional
        />
      </Box>
    </>
  );
}

export function PastJobsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconDollarSignCircleFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
            Your past jobs
          </Heading>
          <Paragraph>
            Include all jobs from the past 2 years. Rental managers ask for this to see an applicant’s job stability and
            history.
          </Paragraph>
        </>
      }
    />
  );
}
